import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import Sidebar from '../components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import {
  Search,
  Plus,
  Users,
  Phone,
  Mail,
  MapPin,
  Edit,
  Trash2,
  FileText,
  MessageCircle,
  User,
  Building,
  StickyNote,
  X,
  Check,
  ChevronRight,
} from 'lucide-react';
import { formatMoney } from '../utils/formatCurrency';
import { getTranslation } from '../i18n/translations';

export default function CustomersPage({ user, logout, lang = 'tr', setLang }) {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    notes: '',
    tax_office: '',
    tax_number: '',
    trade_title: '',
    mersis_no: '',
    website: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Eski localStorage müşterilerini backend'e taşı (tek seferlik)
      const saved = JSON.parse(localStorage.getItem('nkt_customers') || '[]');
      if (saved.length > 0) {
        try {
          await Promise.all(saved.map((c) => api.post('/customers', {
            name: c.name || '',
            email: c.email || '',
            phone: c.phone || '',
            address: c.address || '',
            company: c.company || '',
            notes: c.notes || '',
          })));
          localStorage.removeItem('nkt_customers');
        } catch (_) {}
      }

      const [custRes, propRes] = await Promise.all([
        api.get('/customers'),
        api.get('/proposals'),
      ]);
      setProposals(propRes.data);

      // Teklif istatistiklerini müşterilerle eşleştir (email veya isim üzerinden)
      const stats = new Map();
      propRes.data.forEach((p) => {
        if (!p.customer_name) return;
        const key = (p.customer_email || p.customer_name).toLowerCase();
        const s = stats.get(key) || { proposalCount: 0, totalAmount: 0, lastProposal: null };
        s.proposalCount += 1;
        s.totalAmount += p.grand_total || 0;
        if (!s.lastProposal || new Date(p.created_at) > new Date(s.lastProposal)) {
          s.lastProposal = p.created_at;
        }
        stats.set(key, s);
      });

      const merged = custRes.data.map((c) => {
        const key = (c.email || c.name || '').toLowerCase();
        const s = stats.get(key) || { proposalCount: 0, totalAmount: 0, lastProposal: null };
        return { ...c, ...s };
      });
      setCustomers(merged);
    } catch (error) {
      toast.error(t('dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;
    const term = searchTerm.toLowerCase();
    return customers.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.includes(term) ||
      c.company?.toLowerCase().includes(term)
    );
  }, [customers, searchTerm]);

  const handleOpenModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        company: customer.company || '',
        notes: customer.notes || '',
        tax_office: customer.tax_office || '',
        tax_number: customer.tax_number || '',
        trade_title: customer.trade_title || '',
        mersis_no: customer.mersis_no || '',
        website: customer.website || '',
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '', email: '', phone: '', address: '', company: '', notes: '',
        tax_office: '', tax_number: '', trade_title: '', mersis_no: '', website: '',
      });
    }
    setShowModal(true);
  };

  const handleSaveCustomer = async () => {
    if (!formData.name.trim()) {
      toast.error(t('customerNameRequired'));
      return;
    }

    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success(t('customerUpdated'));
      } else {
        await api.post('/customers', formData);
        toast.success(t('customerAdded'));
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('customerSaveFailed'));
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`"${customer.name}" ${t('confirmDeleteCustomer')}`)) return;

    try {
      await api.delete(`/customers/${customer.id}`);
      toast.success(t('customerDeleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('customerDeleteFailed'));
    }
  };

  const getCustomerProposals = (customer) => {
    return proposals.filter(p => 
      p.customer_email === customer.email || 
      p.customer_name === customer.name
    );
  };

  const formatCurrency = (amount) => formatMoney(amount || 0, lang);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('navCustomers')}</h1>
                <p className="text-slate-500 text-xs sm:text-sm">{t('customersSubtitle')}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
                <Button 
                  onClick={() => handleOpenModal()}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm"
                  data-testid="add-customer-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('newCustomer')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 sm:p-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalCustomers')}</p>
                    <p className="text-2xl font-bold text-slate-900">{customers.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalProposals')}</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {customers.reduce((sum, c) => sum + (c.proposalCount || 0), 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalAmount')}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(customers.reduce((sum, c) => sum + (c.totalAmount || 0), 0))}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">₺</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t('searchCustomerPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Customer Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  {searchTerm ? t('noSearchResults') : t('noCustomersYet')}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => handleOpenModal()}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addFirstCustomer')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCustomers.map((customer) => (
                <Card 
                  key={customer.id} 
                  className="bg-white border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedCustomer(customer)}
                  data-testid={`customer-card-${customer.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                          {customer.name?.charAt(0)?.toUpperCase() || 'M'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{customer.name}</h3>
                          {customer.company && (
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {customer.company}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleOpenModal(customer); }}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer); }}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <a 
                            href={`tel:${customer.phone}`} 
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-blue-600"
                          >
                            {customer.phone}
                          </a>
                          <a
                            href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-auto text-green-600 hover:text-green-700"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a 
                            href={`mailto:${customer.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:text-blue-600 truncate"
                          >
                            {customer.email}
                          </a>
                        </div>
                      )}
                      {customer.address && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="truncate">{customer.address}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between text-sm">
                      <div>
                        <p className="text-slate-500">{t('proposalsLabel')}</p>
                        <p className="font-semibold text-slate-900">{customer.proposalCount || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500">{t('total')}</p>
                        <p className="font-semibold text-emerald-600">{formatCurrency(customer.totalAmount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Add/Edit Customer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? t('editCustomer') : t('addNewCustomer')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('customerName')} *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('fullNamePlaceholder')}
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('companyLabel')}
              </label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder={t('companyOptionalPlaceholder')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('phone')}
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+90 5XX XXX XX XX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('email')}
                </label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                  type="email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('address')}
              </label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder={t('fullAddressPlaceholder')}
              />
            </div>

            {/* Vergi & Ticari Bilgiler */}
            <div className="pt-2 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mt-3 mb-3">{t('fiscalInfoTitle')}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('taxOffice')}
                  </label>
                  <Input
                    value={formData.tax_office}
                    onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })}
                    placeholder={t('taxOfficePlaceholder')}
                    data-testid="customer-tax-office-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('taxNumber')}
                  </label>
                  <Input
                    value={formData.tax_number}
                    onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                    placeholder={t('taxNumberPlaceholder')}
                    data-testid="customer-tax-number-input"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t('tradeTitle')}
                </label>
                <Input
                  value={formData.trade_title}
                  onChange={(e) => setFormData({ ...formData, trade_title: e.target.value })}
                  placeholder={t('tradeTitlePlaceholder')}
                  data-testid="customer-trade-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('mersisNoOptional')}
                  </label>
                  <Input
                    value={formData.mersis_no}
                    onChange={(e) => setFormData({ ...formData, mersis_no: e.target.value })}
                    placeholder={t('mersisNoPlaceholder')}
                    data-testid="customer-mersis-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('websiteOptional')}
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder={t('websitePlaceholder')}
                    type="url"
                    data-testid="customer-website-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('notes')}
              </label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('customerNotesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveCustomer}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-customer-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                {selectedCustomer?.name?.charAt(0)?.toUpperCase() || 'M'}
              </div>
              {selectedCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-6 py-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedCustomer.phone && (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Phone className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-xs text-slate-500">Telefon</p>
                      <a href={`tel:${selectedCustomer.phone}`} className="text-slate-900 hover:text-blue-600">
                        {selectedCustomer.phone}
                      </a>
                    </div>
                  </div>
                )}
                {selectedCustomer.email && (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                    <Mail className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-xs text-slate-500">E-posta</p>
                      <a href={`mailto:${selectedCustomer.email}`} className="text-slate-900 hover:text-blue-600 text-sm">
                        {selectedCustomer.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {selectedCustomer.address && (
                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Adres</p>
                    <p className="text-slate-900">{selectedCustomer.address}</p>
                  </div>
                </div>
              )}

              {/* Vergi & Ticari Bilgiler */}
              {(selectedCustomer.tax_office || selectedCustomer.tax_number || selectedCustomer.trade_title || selectedCustomer.mersis_no || selectedCustomer.website) && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t('fiscalInfoTitle')}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    {selectedCustomer.tax_office && (
                      <div><span className="text-slate-500">{t('taxOffice')}: </span><span className="text-slate-900">{selectedCustomer.tax_office}</span></div>
                    )}
                    {selectedCustomer.tax_number && (
                      <div><span className="text-slate-500">{t('taxNumber')}: </span><span className="text-slate-900 font-mono">{selectedCustomer.tax_number}</span></div>
                    )}
                    {selectedCustomer.trade_title && (
                      <div className="col-span-2"><span className="text-slate-500">{t('tradeTitle')}: </span><span className="text-slate-900">{selectedCustomer.trade_title}</span></div>
                    )}
                    {selectedCustomer.mersis_no && (
                      <div><span className="text-slate-500">{t('mersisNo')}: </span><span className="text-slate-900 font-mono">{selectedCustomer.mersis_no}</span></div>
                    )}
                    {selectedCustomer.website && (
                      <div><span className="text-slate-500">{t('website')}: </span><a href={selectedCustomer.website.startsWith('http') ? selectedCustomer.website : `https://${selectedCustomer.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{selectedCustomer.website}</a></div>
                    )}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{selectedCustomer.proposalCount || 0}</p>
                  <p className="text-sm text-slate-600">{t('totalProposals')}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(selectedCustomer.totalAmount)}</p>
                  <p className="text-sm text-slate-600">{t('totalAmount')}</p>
                </div>
              </div>

              {/* Proposals List */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">{t('proposalsLabel')}</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {getCustomerProposals(selectedCustomer).length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">{t('noProposalsYet')}</p>
                  ) : (
                    getCustomerProposals(selectedCustomer).map((proposal) => (
                      <div 
                        key={proposal.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition"
                        onClick={() => { setSelectedCustomer(null); navigate(`/proposal/${proposal.id}`); }}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-slate-900">{proposal.title || t('untitledProposal')}</p>
                            <p className="text-xs text-slate-500">{formatDate(proposal.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{formatCurrency(proposal.grand_total)}</p>
                          <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => {
                    setSelectedCustomer(null);
                    navigate('/proposal/new');
                  }}
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('createNewProposal')}
                </Button>
                {selectedCustomer.phone && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(`https://wa.me/${selectedCustomer.phone.replace(/\D/g, '')}`, '_blank')}
                    className="text-green-600 border-green-600 hover:bg-green-50"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
